/**
 * request 网络请求工具
 * 更详细的 api 文档: https://github.com/umijs/umi-request
 */
import React from 'react'
import { extend } from 'umi-request'
import { notification } from 'antd'
import { history } from 'umi'
import { accountLogout } from '@/services/system/account'

// 需要重新登录的code
const needLoginCode = [1001, 1002, 2001, 50018]

// 不需要提示的code
const noTipCode = [0, 41002, 201, 106, 52526]

const statusMessage = {
  200: '服务器成功返回请求的数据。',
  201: '新建或修改数据成功。',
  202: '一个请求已经进入后台排队（异步任务）。',
  204: '删除数据成功。',
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。'
}

const noTipsStatus = [401]

const errorCodeMessage = {
  104: '上传文件最大不超过15个，请调整',
  1001: '登录非法',
  1002: '用户未登录',
  1003: '密码错误',
  2001: '暂无访问接口的权限',
  2002: '暂无下载文件的权限',
  3001: '数据库异常',
  3002: '缓存服务异常',
  3003: '文件存储服务异常',
  3004: '未知错误',
  4001: '参数非法',
  4002: '手机号非法',
  4003: '密码格式非法',
  52201: '你已经添加过该项目，请勿重复添加'
}

// 响应不需要提示信息的接口
const noTipsApi = [
  '/api/v2/system/account_auth',
  '/api/v1/view',
  '/api/v2/project/qmp/get',
  // '/api/v1/project/like_discuss',
  // '/api/v1/project/unlike_discuss',
  '/api/v2/project/portfolio/manage_get',
  '/api/v2/system/performance_management/performance_employee_list_record'
]

/**
 * 系统错误处理
 */
const handleErrorCode = (
  code: number,
  msg: string,
  url: string,
  options: { hideError: boolean }
) => {
  const { hideError } = options
  const isExist = noTipsApi.some(noUrl => {
    return url.indexOf(noUrl) !== -1
  })
  if (isExist) return

  // 不需要提示信息的的code
  if (needLoginCode.includes(code) || code === 401) {
    const { pathname, search, hash } = window.location
    const referer = `${pathname}${search}${hash}`

    if (pathname === '/binding') {
      return
    }
    // 需要重新登录
    if (pathname !== '/user/login') {
      logout().then(() => {
        history.replace({
          pathname: '/user/login',
          query: {
            referer
          }
        })
      })
    }
    return
  }
  if (noTipCode.includes(code)) {
    return
  }
  if (!hideError) {
    if (errorCodeMessage[code]) {
      notification.error({
        message: errorCodeMessage[code] || msg || '系统开小差了!'
      })
    } else {
      notification.error({
        message: msg ? <div dangerouslySetInnerHTML={{ __html: msg }} /> : '系统开小差了!'
      })
    }
  }
}

/**
 * 异常处理程序
 */
const errorHandler = (error: { response: Response }): Response => {
  const { response } = error
  // console.log(error)

  if (response && response.status) {
    const errorText = statusMessage[response.status] || response.statusText
    const { status, url } = response

    const isExist =
      noTipsApi.some(noUrl => {
        return url.indexOf(noUrl) !== -1
      }) || noTipsStatus.includes(response.status)

    if (!isExist) {
      notification.error({
        message: `请求错误 ${status}: ${url}`,
        description: errorText
      })
    }
  } else if (!response) {
    notification.error({
      message: '您的网络发生异常，无法连接服务器'
    })
  }
  return response || {}
}

/**
 * 配置request请求时的默认参数
 */
const request = extend({
  errorHandler, // 默认错误处理
  credentials: 'include' // 默认请求是否带上cookie
})

// request拦截器, 改变url 或 options.
request.interceptors.request.use((url, options) => {
  if (url && options.method?.toLowerCase() === 'get') {
    url = `${url + (url.indexOf('?') === -1 ? '?' : '&')}t=${+new Date().getTime()}`
    for (const key in options.data) {
      const item = options.data[key]
      if (item === undefined) {
        continue
      }
      if (item instanceof Array) {
        item.forEach(_item => {
          url += `&${key}=${_item}`
        })
      } else {
        url += `&${key}=${item}`
      }
    }
  }
  return {
    url,
    options: {
      ...options,
      headers: {
        ...options.headers,
        ClientType: 'PC'
      }
    }
  }
})

// response拦截器, 处理response
request.interceptors.response.use(async (response, options) => {
  let res: any
  try {
    res = await response.clone().json()
  } catch (error) {
    res = await response.clone().blob()
    return res
  }
  if (!res.code && response.url.includes('/api/v2')) {
    return { code: 0, data: res }
  }
  if (res.code === 0) {
    return response
  }
  const { hideError } = options
  handleErrorCode(res.code, res.msg || res.message, response.url, { hideError: hideError })
  return response
})

// 登录
const logout = async () => {
  await accountLogout()
}

export default request
